import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { navigableProps } from '../utils/navigation';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HistoryIcon from '@mui/icons-material/History';
import { format } from 'date-fns';
import PageHeader from '../components/common/PageHeader';
import FormSkeleton from '../components/common/FormSkeleton';
import ReadOnlyField from '../components/common/ReadOnlyField';
import ConfirmDialog from '../components/common/ConfirmDialog';
import HistorieTimeline from '../components/common/HistorieTimeline';
import DiskussionContextLink from '../components/diskussion/DiskussionContextLink';
import { useNotification } from '../hooks/NotificationContext';
import DokumentenAblage from '../components/common/DokumentenAblage';
import { kundenService } from '../services/kundenService';
import { ansprechpartnerService } from '../services/ansprechpartnerService';
import angeboteService from '../services/angeboteService';
import { usePermissions } from '../hooks/usePermissions';
import * as P from '../constants/permissions';
import { Kunde, KundeCreate, Ansprechpartner, AnsprechpartnerCreate, KundeOverview, RechnungsaufteilungsAnteil, FileEntity, AngebotList } from '../types';
import { LAENDER, laendername } from '../constants/laender';

const emptyAufteilungAnteile = (): RechnungsaufteilungsAnteil[] => ([
  { kunde_id: 0, bezeichnung: 'Fahrschule', prozent: 50 },
  { kunde_id: 0, bezeichnung: 'BAZ', prozent: 50 },
]);

const einzeilig = (wert?: string | null): string => (wert || '').replace(/\s+/g, ' ').trim();

const kundeToFormData = (data: Kunde): KundeCreate => ({
  name_des_unternehmens: data.name_des_unternehmens,
  adresse_des_unternehmens: data.adresse_des_unternehmens || '',
  strasse: data.strasse || '',
  plz: data.plz || '',
  ort: data.ort || '',
  rechnungskontakt: data.rechnungskontakt || '',
  email: data.email || '',
  land: data.land || 'DE',
  ust_id: data.ust_id || '',
  leitweg_id: data.leitweg_id || '',
  lieferantennummer: data.lieferantennummer || '',
  rechnungsaufteilung_aktiv: data.rechnungsaufteilung_aktiv || false,
  rechnungsaufteilung_anteile: data.rechnungsaufteilung_anteile?.length
    ? data.rechnungsaufteilung_anteile
    : emptyAufteilungAnteile(),
  leistungsnachweis_an_rechnung: data.leistungsnachweis_an_rechnung || false,
  leistungsnachweis_vorlage: data.leistungsnachweis_vorlage || 'stark',
});

interface SmartInsight {
  severity: 'error' | 'warning' | 'info' | 'success';
  title: string;
  description: string;
}

const formatCurrency = (value: number): string =>
  `${value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR`;

const KundeDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const { hasPermission } = usePermissions();
  const isNew = id === 'neu';

  const [kunde, setKunde] = useState<Kunde | null>(null);
  const [ansprechpartner, setAnsprechpartner] = useState<Ansprechpartner[]>([]);
  const [angebote, setAngebote] = useState<AngebotList[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(isNew);
  const [smartLookupLoading, setSmartLookupLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [filesToDelete, setFilesToDelete] = useState<FileEntity[]>([]);
  const [loadingFilesToDelete, setLoadingFilesToDelete] = useState(false);
  const [ansprechpartnerDialogOpen, setAnsprechpartnerDialogOpen] = useState(false);
  const [editingAnsprechpartner, setEditingAnsprechpartner] = useState<Ansprechpartner | null>(null);
  const [deleteAnsprechpartnerDialogOpen, setDeleteAnsprechpartnerDialogOpen] = useState(false);
  const [ansprechpartnerToDelete, setAnsprechpartnerToDelete] = useState<Ansprechpartner | null>(null);
  const [historieOpen, setHistorieOpen] = useState(false);
  const [overview, setOverview] = useState<KundeOverview | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState<string | null>(null);

  const [alleKunden, setAlleKunden] = useState<Kunde[]>([]);
  const [formData, setFormData] = useState<KundeCreate>({
    name_des_unternehmens: '',
    adresse_des_unternehmens: '',
    strasse: '',
    plz: '',
    ort: '',
    rechnungskontakt: '',
    email: '',
    land: 'DE',
    ust_id: '',
    leitweg_id: '',
    lieferantennummer: '',
    rechnungsaufteilung_aktiv: false,
    rechnungsaufteilung_anteile: emptyAufteilungAnteile(),
    leistungsnachweis_an_rechnung: false,
    leistungsnachweis_vorlage: 'stark',
  });

  const [ansprechpartnerForm, setAnsprechpartnerForm] = useState<AnsprechpartnerCreate>({
    vorname: '',
    nachname: '',
    email: '',
    telefonnummer: '',
    mobilnummer: '',
    position_funktion: '',
    abteilung: '',
    buero_standort: '',
    notizen: '',
    kunde_id: 0,
  });

  const smartInsights = useMemo<SmartInsight[]>(() => {
    if (!overview) {
      return [];
    }

    const insights: SmartInsight[] = [];
    const overdue = overview.billing.overdue_total;
    const open = overview.billing.open_total;
    const paidRatio =
      overview.billing.revenue_total > 0
        ? overview.billing.paid_total / overview.billing.revenue_total
        : 0;

    if (overdue > 0) {
      insights.push({
        severity: 'error',
        title: 'Überfällige Rechnungen',
        description: `${overview.billing.overdue_count} Rechnung(en) sind überfällig, Gesamtbetrag ${formatCurrency(overdue)}.`,
      });
    }

    if (overview.audits.stale_count > 0) {
      insights.push({
        severity: 'warning',
        title: 'Audit-Stagnation',
        description: `${overview.audits.stale_count} laufende Audits ohne Aktivität seit mindestens 14 Tagen.`,
      });
    }

    if (open > 0 && overdue === 0) {
      insights.push({
        severity: 'info',
        title: 'Offene Forderungen',
        description: `Aktuell sind ${formatCurrency(open)} im Status "Versendet".`,
      });
    }

    if (overview.projects.total === 0) {
      insights.push({
        severity: 'info',
        title: 'Noch keine Projekte',
        description: 'Für diesen Kunden wurden bisher keine Projekte angelegt.',
      });
    } else if (paidRatio >= 0.8) {
      insights.push({
        severity: 'success',
        title: 'Starke Zahlungsquote',
        description: `${Math.round(paidRatio * 100)}% des Umsatzes sind bereits bezahlt.`,
      });
    }

    return insights.slice(0, 3);
  }, [overview]);

  useEffect(() => {
    if (!isNew && id) {
      const loadKunde = async () => {
        try {
          setLoading(true);
          const data = await kundenService.get(parseInt(id));
          setKunde(data);
          setFormData(kundeToFormData(data));
          await Promise.all([
            loadAnsprechpartner(parseInt(id)),
            loadAngebote(parseInt(id)),
            loadKundeUebersicht(parseInt(id)),
          ]);
        } catch {
          // Interceptor handles error display
        } finally {
          setLoading(false);
        }
      };
      loadKunde();
    }
  }, [id, isNew]);

  useEffect(() => {
    const loadAlleKunden = async () => {
      try {
        setAlleKunden(await kundenService.list());
      } catch {
        // Interceptor handles error display
      }
    };
    loadAlleKunden();
  }, []);

  const loadAnsprechpartner = async (kundeId: number) => {
    try {
      const data = await ansprechpartnerService.list({ kunde_id: kundeId });
      setAnsprechpartner(data);
    } catch {
      // Interceptor handles error display
    }
  };

  const loadAngebote = async (kundeId: number) => {
    if (!hasPermission(P.ANGEBOTE_VIEW)) return;
    try {
      setAngebote(await angeboteService.list({ kunde_id: kundeId }));
    } catch {
      // Interceptor handles error display
    }
  };

  const loadKundeUebersicht = async (kundeId: number) => {
    try {
      setOverviewLoading(true);
      setOverviewError(null);
      const data = await kundenService.getUebersicht(kundeId);
      setOverview(data);
    } catch {
      setOverviewError('Übersichtsdaten konnten nicht geladen werden.');
    } finally {
      setOverviewLoading(false);
    }
  };

  const handleInputChange = (field: keyof KundeCreate) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleSmartLookup = async () => {
    if (!formData.name_des_unternehmens.trim()) return;
    try {
      setSmartLookupLoading(true);
      const result = await kundenService.smartLookup(formData.name_des_unternehmens);
      setFormData((prev) => ({
        ...prev,
        name_des_unternehmens: result.name_des_unternehmens || prev.name_des_unternehmens,
        adresse_des_unternehmens: result.adresse_des_unternehmens || prev.adresse_des_unternehmens,
        strasse: result.strasse || prev.strasse,
        plz: result.plz || prev.plz,
        ort: result.ort || prev.ort,
        rechnungskontakt: result.rechnungskontakt || prev.rechnungskontakt,
        email: result.email || prev.email,
      }));
      showSuccess('Firmendaten erfolgreich ermittelt');
    } catch {
      // Interceptor handles error display
    } finally {
      setSmartLookupLoading(false);
    }
  };

  const handleAnsprechpartnerInputChange = (field: keyof AnsprechpartnerCreate) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setAnsprechpartnerForm({ ...ansprechpartnerForm, [field]: e.target.value });
  };

  const handleStartEditing = () => {
    setEditing(true);
  };

  const handleCancelEditing = () => {
    if (kunde) {
      setFormData(kundeToFormData(kunde));
    }
    setEditing(false);
  };

  const handleAufteilungToggle = (checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      rechnungsaufteilung_aktiv: checked,
      rechnungsaufteilung_anteile:
        prev.rechnungsaufteilung_anteile?.length === 2
          ? prev.rechnungsaufteilung_anteile
          : emptyAufteilungAnteile(),
    }));
  };

  const handleAnteilChange = (index: number, field: keyof RechnungsaufteilungsAnteil, value: string | number) => {
    setFormData((prev) => {
      const anteile = [...(prev.rechnungsaufteilung_anteile || emptyAufteilungAnteile())];
      const current = { ...anteile[index] };
      if (field === 'prozent') {
        const pct = Math.max(0, Math.min(100, Number(value) || 0));
        current.prozent = pct;
        const otherIndex = index === 0 ? 1 : 0;
        anteile[otherIndex] = { ...anteile[otherIndex], prozent: Math.round((100 - pct) * 100) / 100 };
      } else if (field === 'kunde_id') {
        current.kunde_id = Number(value) || 0;
      } else {
        current.bezeichnung = String(value);
      }
      anteile[index] = current;
      return { ...prev, rechnungsaufteilung_anteile: anteile };
    });
  };

  const aufteilungAnteile = formData.rechnungsaufteilung_anteile || emptyAufteilungAnteile();
  const kundeNameById = (kundeId: number) =>
    einzeilig(alleKunden.find((k) => k.id === kundeId)?.name_des_unternehmens) || (kundeId ? `#${kundeId}` : '–');

  const handleSave = async () => {
    if (formData.rechnungsaufteilung_aktiv) {
      const anteile = formData.rechnungsaufteilung_anteile || [];
      if (anteile.length !== 2 || anteile.some((a) => !a.kunde_id || !a.bezeichnung.trim())) {
        showError('Rechnungsaufteilung braucht zwei Empfänger mit Bezeichnung.');
        return;
      }
      const summe = anteile.reduce((s, a) => s + Number(a.prozent), 0);
      if (Math.abs(summe - 100) > 0.01) {
        showError('Die Prozentanteile müssen in Summe 100 ergeben.');
        return;
      }
      if (anteile[0].kunde_id === anteile[1].kunde_id) {
        showError('Die Empfänger der Anteile müssen unterschiedlich sein.');
        return;
      }
    }
    try {
      setSaving(true);
      if (isNew) {
        await kundenService.create(formData);
        showSuccess('Kunde erfolgreich erstellt');
        setTimeout(() => navigate('/kunden'), 1000);
      } else if (id) {
        const updated = await kundenService.update(parseInt(id), formData);
        setKunde(updated);
        showSuccess('Kunde erfolgreich aktualisiert');
        setEditing(false);
      }
    } catch {
      // Interceptor handles error display
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || isNew) return;
    try {
      await kundenService.delete(parseInt(id));
      showSuccess('Kunde erfolgreich gelöscht');
      setTimeout(() => navigate('/kunden'), 1000);
    } catch {
      // Interceptor handles error display
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const resetAnsprechpartnerForm = () => {
    setAnsprechpartnerForm({
      vorname: '',
      nachname: '',
      email: '',
      telefonnummer: '',
      mobilnummer: '',
      position_funktion: '',
      abteilung: '',
      buero_standort: '',
      notizen: '',
      kunde_id: 0,
    });
    setEditingAnsprechpartner(null);
  };

  const handleOpenAnsprechpartnerDialog = (ap?: Ansprechpartner) => {
    if (ap) {
      // Bearbeiten
      setEditingAnsprechpartner(ap);
      setAnsprechpartnerForm({
        vorname: ap.vorname,
        nachname: ap.nachname,
        email: ap.email || '',
        telefonnummer: ap.telefonnummer || '',
        mobilnummer: ap.mobilnummer || '',
        position_funktion: ap.position_funktion || '',
        abteilung: ap.abteilung || '',
        buero_standort: ap.buero_standort || '',
        notizen: ap.notizen || '',
        kunde_id: ap.kunde_id,
      });
    } else {
      // Neu anlegen
      resetAnsprechpartnerForm();
    }
    setAnsprechpartnerDialogOpen(true);
  };

  const handleSaveAnsprechpartner = async () => {
    if (!id || isNew) return;
    try {
      if (editingAnsprechpartner) {
        // Aktualisieren
        await ansprechpartnerService.update(editingAnsprechpartner.id, {
          ...ansprechpartnerForm,
          kunde_id: parseInt(id),
        });
        showSuccess('Ansprechpartner erfolgreich aktualisiert');
      } else {
        // Neu anlegen
        const data = { ...ansprechpartnerForm, kunde_id: parseInt(id) };
        await ansprechpartnerService.create(data);
        showSuccess('Ansprechpartner erfolgreich hinzugefügt');
      }
      await loadAnsprechpartner(parseInt(id));
      setAnsprechpartnerDialogOpen(false);
      resetAnsprechpartnerForm();
    } catch {
      // Interceptor handles error display
    }
  };

  const handleDeleteAnsprechpartnerClick = (ap: Ansprechpartner) => {
    setAnsprechpartnerToDelete(ap);
    setDeleteAnsprechpartnerDialogOpen(true);
  };

  const handleDeleteAnsprechpartnerConfirm = async () => {
    if (!ansprechpartnerToDelete) return;
    try {
      await ansprechpartnerService.delete(ansprechpartnerToDelete.id);
      if (id) {
        await loadAnsprechpartner(parseInt(id));
      }
      showSuccess('Ansprechpartner erfolgreich gelöscht');
    } catch {
      // Interceptor handles error display
    } finally {
      setDeleteAnsprechpartnerDialogOpen(false);
      setAnsprechpartnerToDelete(null);
    }
  };

  if (loading) {
    return <FormSkeleton fields={4} />;
  }

  return (
    <Box>
      <PageHeader
        title={isNew ? 'Neuer Kunde' : einzeilig(kunde?.name_des_unternehmens) || 'Kunde'}
        breadcrumbs={[
          { label: 'Kunden', path: '/kunden' },
          { label: isNew ? 'Neu' : einzeilig(kunde?.name_des_unternehmens) || 'Kunde' },
        ]}
      />

      {!isNew && id && (
        <DiskussionContextLink
          anchor={{ refType: 'kunde', refId: parseInt(id, 10) }}
          objectLabel={einzeilig(kunde?.name_des_unternehmens)}
        />
      )}

      {!isNew && id && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Tooltip title="Änderungshistorie">
            <IconButton onClick={() => setHistorieOpen(true)}>
              <HistoryIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {!isNew && id && (
        <Box sx={{ mb: 4 }}>
          {overviewLoading && !overview && (
            <Card sx={{ mb: 2 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={22} />
                <Typography color="text.secondary">Übersichtsdaten werden geladen…</Typography>
              </CardContent>
            </Card>
          )}

          {overviewError && !overview && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {overviewError}
            </Alert>
          )}

          {overview && (
            <>
              {smartInsights.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  {smartInsights.map((insight, idx) => (
                    <Alert key={`${insight.title}-${idx}`} severity={insight.severity} sx={{ mb: idx < smartInsights.length - 1 ? 1 : 0 }}>
                      <strong>{insight.title}:</strong> {insight.description}
                    </Alert>
                  ))}
                </Box>
              )}

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={4} md={2}>
                  <Card>
                    <CardContent sx={{ py: 2 }}>
                      <Typography variant="body2" color="text.secondary">Projekte gesamt</Typography>
                      <Typography variant="h5">{overview.projects.total}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Card>
                    <CardContent sx={{ py: 2 }}>
                      <Typography variant="body2" color="text.secondary">Aktive Projekte</Typography>
                      <Typography variant="h5" color="primary.main">{overview.projects.active}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Card>
                    <CardContent sx={{ py: 2 }}>
                      <Typography variant="body2" color="text.secondary">Umsatz gesamt</Typography>
                      <Typography variant="h6">{formatCurrency(overview.billing.revenue_total)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Card>
                    <CardContent sx={{ py: 2 }}>
                      <Typography variant="body2" color="text.secondary">Offen</Typography>
                      <Typography variant="h6" color="primary.main">{formatCurrency(overview.billing.open_total)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Card>
                    <CardContent sx={{ py: 2 }}>
                      <Typography variant="body2" color="text.secondary">Überfällig</Typography>
                      <Typography variant="h6" color={overview.billing.overdue_total > 0 ? 'error.main' : 'text.primary'}>
                        {formatCurrency(overview.billing.overdue_total)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {overview.billing.overdue_count} Rechnung(en)
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Card>
                    <CardContent sx={{ py: 2 }}>
                      <Typography variant="body2" color="text.secondary">Laufende Audits</Typography>
                      <Typography variant="h5">{overview.audits.running}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2 }}>Finanzen</Typography>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Bezahlt</Typography>
                          <Typography variant="subtitle1" color="success.main">{formatCurrency(overview.billing.paid_total)}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Offener Betrag</Typography>
                          <Typography variant="subtitle1">{formatCurrency(overview.billing.open_total)}</Typography>
                        </Grid>
                      </Grid>

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        {overview.billing.status_breakdown.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">Keine Rechnungen vorhanden.</Typography>
                        ) : (
                          overview.billing.status_breakdown.map((item) => (
                            <Chip
                              key={item.status}
                              size="small"
                              label={`${item.status}: ${item.count}`}
                              color={item.status === 'Bezahlt' ? 'success' : item.status === 'Versendet' ? 'primary' : 'default'}
                              variant="outlined"
                            />
                          ))
                        )}
                      </Box>

                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Nächste Fälligkeiten</Typography>
                      {overview.billing.next_due_invoices.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">Keine offenen Rechnungen.</Typography>
                      ) : (
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Nr.</TableCell>
                                <TableCell>Fällig am</TableCell>
                                <TableCell align="right">Brutto</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {overview.billing.next_due_invoices.map((invoice) => (
                                <TableRow key={invoice.id}>
                                  <TableCell>{invoice.rechnungsnummer}</TableCell>
                                  <TableCell>{format(new Date(invoice.faellig_am), 'dd.MM.yyyy')}</TableCell>
                                  <TableCell align="right">{formatCurrency(invoice.bruttobetrag)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2 }}>Audits & Aktivität</Typography>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">Audits gesamt</Typography>
                          <Typography variant="h6">{overview.audits.total}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">Abgeschlossen</Typography>
                          <Typography variant="h6">{overview.audits.completed}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">Stagnierend</Typography>
                          <Typography variant="h6" color={overview.audits.stale_count > 0 ? 'warning.main' : 'text.primary'}>
                            {overview.audits.stale_count}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        {overview.audits.by_status.map((status) => (
                          <Chip key={status.status} size="small" label={`${status.status}: ${status.count}`} />
                        ))}
                      </Box>

                      <Typography variant="body2" color="text.secondary">
                        Änderungen letzte 30 Tage: <strong>{overview.activity.last_30_days_changes}</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Letzte Aktivität:{' '}
                        <strong>
                          {overview.activity.last_change_at
                            ? format(new Date(overview.activity.last_change_at), 'dd.MM.yyyy HH:mm')
                            : '-'}
                        </strong>
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2 }}>Top-Projekte</Typography>
                      {overview.top_projects.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">Keine Projekte vorhanden.</Typography>
                      ) : (
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Projekt</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Audits</TableCell>
                                <TableCell align="right">Rechnungen</TableCell>
                                <TableCell align="right">Umsatz</TableCell>
                                <TableCell align="right">Offen</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {overview.top_projects.map((project) => (
                                <TableRow
                                  key={project.id}
                                  hover
                                  sx={{ cursor: 'pointer' }}
                                  {...navigableProps(navigate, `/projekte/${project.id}`)}
                                >
                                  <TableCell>{project.name}</TableCell>
                                  <TableCell>{project.status_name || '-'}</TableCell>
                                  <TableCell align="right">{project.audits_count}</TableCell>
                                  <TableCell align="right">{project.rechnungen_count}</TableCell>
                                  <TableCell align="right">{formatCurrency(project.umsatz_brutto)}</TableCell>
                                  <TableCell align="right">{formatCurrency(project.open_brutto)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      )}

      <Card>
        <CardContent>
          {/* ── Read-only view ─────────────────────────────── */}
          {!editing && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <ReadOnlyField label="Name des Unternehmens" value={formData.name_des_unternehmens} multiline />
              </Grid>
              <Grid item xs={12}>
                <ReadOnlyField label="Strasse" value={formData.strasse} />
              </Grid>
              <Grid item xs={4} sm={3}>
                <ReadOnlyField label="PLZ" value={formData.plz} />
              </Grid>
              <Grid item xs={8} sm={9}>
                <ReadOnlyField label="Ort" value={formData.ort} />
              </Grid>
              <Grid item xs={4} sm={3}>
                <ReadOnlyField label="Land" value={`${formData.land || 'DE'} – ${laendername(formData.land)}`} />
              </Grid>
              <Grid item xs={8} sm={9}>
                <ReadOnlyField label="USt-ID" value={formData.ust_id} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
                  E-Rechnung / öffentlicher Auftraggeber
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <ReadOnlyField label="Leitweg-ID" value={formData.leitweg_id} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ReadOnlyField label="Lieferantennummer" value={formData.lieferantennummer} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ReadOnlyField label="Rechnungskontakt" value={formData.rechnungskontakt} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ReadOnlyField label="E-Mail" value={formData.email} />
              </Grid>
              {!isNew && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                    Rechnungsaufteilung
                  </Typography>
                  {formData.rechnungsaufteilung_aktiv ? (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Entwurfsrechnungen dieses Kunden können auf zwei Empfänger-Firmen aufgeteilt werden.
                      </Typography>
                      <Grid container spacing={1}>
                        {aufteilungAnteile.map((anteil, idx) => (
                          <Grid item xs={12} sm={6} key={idx}>
                            <ReadOnlyField
                              label={anteil.bezeichnung || `Anteil ${idx + 1}`}
                              value={`${kundeNameById(anteil.kunde_id)} · ${anteil.prozent} %`}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Nicht aktiv. Beim Bearbeiten können zwei Empfänger (z. B. Fahrschule und BAZ) hinterlegt werden.
                    </Typography>
                  )}
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                  Leistungsnachweis auf der Rechnung
                </Typography>
                {formData.leistungsnachweis_an_rechnung ? (
                  <Typography variant="body2" color="text.secondary">
                    Der Leistungsnachweis wird an die Rechnungs-PDF angehängt
                    (Vorlage: {formData.leistungsnachweis_vorlage === 'enbw' ? 'EnBW-Formular' : 'STARK-Design'}).
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Nicht aktiv. Rechnung und Leistungsnachweis bleiben getrennte Dokumente.
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    color="error"
                    disabled={loadingFilesToDelete}
                    onClick={async () => {
                      if (!id) return;
                      setLoadingFilesToDelete(true);
                      try {
                        const files = await kundenService.getFilesToDelete(parseInt(id, 10));
                        setFilesToDelete(files);
                        setDeleteDialogOpen(true);
                      } catch {
                        setFilesToDelete([]);
                        setDeleteDialogOpen(true);
                      } finally {
                        setLoadingFilesToDelete(false);
                      }
                    }}
                  >
                    {loadingFilesToDelete ? <CircularProgress size={20} /> : 'Löschen'}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={handleStartEditing}
                  >
                    Bearbeiten
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}

          {/* ── Edit view ──────────────────────────────────── */}
          {editing && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    label="Name des Unternehmens"
                    value={formData.name_des_unternehmens}
                    onChange={handleInputChange('name_des_unternehmens')}
                    required
                    multiline
                    minRows={2}
                  />
                  <Tooltip title="Firmendaten per KI-Recherche automatisch ermitteln">
                    <span>
                      <Button
                        variant="outlined"
                        onClick={handleSmartLookup}
                        disabled={smartLookupLoading || !formData.name_des_unternehmens.trim()}
                        startIcon={
                          smartLookupLoading ? (
                            <CircularProgress size={18} />
                          ) : (
                            <AutoAwesomeIcon />
                          )
                        }
                        sx={{
                          whiteSpace: 'nowrap',
                          minWidth: 'fit-content',
                          height: 56,
                        }}
                      >
                        {smartLookupLoading ? 'Suche…' : 'Smart Lookup'}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Strasse"
                  value={formData.strasse}
                  onChange={handleInputChange('strasse')}
                />
              </Grid>
              <Grid item xs={4} sm={3}>
                <TextField
                  fullWidth
                  label="PLZ"
                  value={formData.plz}
                  onChange={handleInputChange('plz')}
                />
              </Grid>
              <Grid item xs={8} sm={9}>
                <TextField
                  fullWidth
                  label="Ort"
                  value={formData.ort}
                  onChange={handleInputChange('ort')}
                />
              </Grid>
              <Grid item xs={4} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Land</InputLabel>
                  <Select
                    label="Land"
                    value={formData.land || 'DE'}
                    onChange={(e) => setFormData({ ...formData, land: String(e.target.value) })}
                  >
                    {(!formData.land || LAENDER.some((l) => l.code === formData.land)
                      ? LAENDER
                      : [{ code: formData.land, name: formData.land }, ...LAENDER]
                    ).map((land) => (
                      <MenuItem key={land.code} value={land.code}>
                        {land.code} – {land.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={8} sm={9}>
                <TextField
                  fullWidth
                  label="USt-ID"
                  value={formData.ust_id}
                  onChange={handleInputChange('ust_id')}
                  helperText="Für EU-Reverse-Charge erforderlich"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  E-Rechnung / öffentlicher Auftraggeber
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Leitweg-ID"
                  value={formData.leitweg_id || ''}
                  onChange={handleInputChange('leitweg_id')}
                  helperText="Vom Auftraggeber erfragen; wird als Käuferreferenz (BT-10) übernommen"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Lieferantennummer"
                  value={formData.lieferantennummer || ''}
                  onChange={handleInputChange('lieferantennummer')}
                  helperText="Nur wenn die Behörde uns eine Kreditorennummer mitgeteilt hat"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Rechnungskontakt"
                  value={formData.rechnungskontakt}
                  onChange={handleInputChange('rechnungskontakt')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="E-Mail"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                />
              </Grid>
              {!isNew && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                    Rechnungsaufteilung
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(formData.rechnungsaufteilung_aktiv)}
                        onChange={(e) => handleAufteilungToggle(e.target.checked)}
                      />
                    }
                    label="Rechnung in zwei Empfänger-Firmen aufteilen"
                  />
                  {formData.rechnungsaufteilung_aktiv && (
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                      {aufteilungAnteile.map((anteil, idx) => (
                        <Grid item xs={12} sm={6} key={idx}>
                          <TextField
                            fullWidth
                            label="Bezeichnung"
                            value={anteil.bezeichnung}
                            onChange={(e) => handleAnteilChange(idx, 'bezeichnung', e.target.value)}
                            sx={{ mb: 1.5 }}
                          />
                          <FormControl fullWidth sx={{ mb: 1.5 }}>
                            <InputLabel>Empfänger</InputLabel>
                            <Select
                              label="Empfänger"
                              value={anteil.kunde_id || ''}
                              onChange={(e) => handleAnteilChange(idx, 'kunde_id', Number(e.target.value))}
                            >
                              {alleKunden.map((k) => (
                                <MenuItem key={k.id} value={k.id}>
                                  {einzeilig(k.name_des_unternehmens)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField
                            fullWidth
                            label="Anteil (%)"
                            type="number"
                            value={anteil.prozent}
                            onChange={(e) => handleAnteilChange(idx, 'prozent', e.target.value)}
                            inputProps={{ min: 0, max: 100, step: 0.01 }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                  Leistungsnachweis auf der Rechnung
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(formData.leistungsnachweis_an_rechnung)}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          leistungsnachweis_an_rechnung: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Leistungsnachweis an die Rechnungs-PDF anhängen"
                />
                {formData.leistungsnachweis_an_rechnung && (
                  <FormControl fullWidth sx={{ mt: 1.5, maxWidth: 360 }}>
                    <InputLabel>Vorlage</InputLabel>
                    <Select
                      label="Vorlage"
                      value={formData.leistungsnachweis_vorlage || 'stark'}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          leistungsnachweis_vorlage: e.target.value as 'stark' | 'enbw',
                        }))
                      }
                    >
                      <MenuItem value="stark">STARK-Design</MenuItem>
                      <MenuItem value="enbw">EnBW-Formular</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  {!isNew && (
                    <Button variant="outlined" onClick={handleCancelEditing}>
                      Abbrechen
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving || !formData.name_des_unternehmens}
                  >
                    {saving ? <CircularProgress size={24} /> : 'Speichern'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      {!isNew && id && (
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Ansprechpartner</Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleOpenAnsprechpartnerDialog()}
            >
              Hinzufügen
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>E-Mail</TableCell>
                  <TableCell>Telefon</TableCell>
                  <TableCell>Position</TableCell>
                  <TableCell>Erstellt am</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ansprechpartner.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Keine Ansprechpartner gefunden
                    </TableCell>
                  </TableRow>
                ) : (
                  ansprechpartner.map((ap) => (
                    <TableRow key={ap.id}>
                      <TableCell>
                        {ap.vorname} {ap.nachname}
                      </TableCell>
                      <TableCell>{ap.email || '-'}</TableCell>
                      <TableCell>{ap.telefonnummer || ap.mobilnummer || '-'}</TableCell>
                      <TableCell>{ap.position_funktion || '-'}</TableCell>
                      <TableCell>
                        {format(new Date(ap.erstellt_am), 'dd.MM.yyyy')}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenAnsprechpartnerDialog(ap)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteAnsprechpartnerClick(ap)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {!isNew && id && hasPermission(P.ANGEBOTE_VIEW) && (
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Angebote</Typography>
            {hasPermission(P.ANGEBOTE_CREATE) && (
              <Button size="small" startIcon={<AddIcon />} onClick={() => navigate(`/angebote/neu?kunde_id=${id}`)}>
                Neues Angebot
              </Button>
            )}
          </Box>
          {angebote.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Noch keine Angebote für diesen Kunden.</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nummer</TableCell>
                    <TableCell>Titel</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Netto</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {angebote.map((a) => (
                    <TableRow key={a.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/angebote/${a.id}`)}>
                      <TableCell>{a.angebotsnummer}</TableCell>
                      <TableCell>{a.titel}</TableCell>
                      <TableCell>
                        <Chip size="small" label={a.status === 'Zurueckgezogen' ? 'Zurückgezogen' : a.status} />
                      </TableCell>
                      <TableCell align="right">{a.nettobetrag.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {!isNew && id && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Dokumente
          </Typography>
          <DokumentenAblage kundeId={parseInt(id, 10)} />
        </Box>
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Kunde löschen"
        message={
          filesToDelete.length > 0
            ? 'Möchten Sie diesen Kunden wirklich löschen? Dabei werden folgende Dateien unwiderruflich mitgelöscht. Diese Aktion kann nicht rückgängig gemacht werden.'
            : 'Möchten Sie diesen Kunden wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.'
        }
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setFilesToDelete([]);
        }}
        additionalContent={
          filesToDelete.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Mitgelöschte Dateien ({filesToDelete.length}):
              </Typography>
              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 2.5,
                  maxHeight: 120,
                  overflow: 'auto',
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                }}
              >
                {filesToDelete.map((f) => (
                  <li key={f.id}>{f.dateiname}</li>
                ))}
              </Box>
            </Box>
          ) : undefined
        }
      />

      <Dialog
        open={ansprechpartnerDialogOpen}
        onClose={() => {
          setAnsprechpartnerDialogOpen(false);
          resetAnsprechpartnerForm();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingAnsprechpartner ? 'Ansprechpartner bearbeiten' : 'Neuer Ansprechpartner'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Vorname" value={ansprechpartnerForm.vorname} onChange={handleAnsprechpartnerInputChange('vorname')} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Nachname" value={ansprechpartnerForm.nachname} onChange={handleAnsprechpartnerInputChange('nachname')} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="E-Mail" type="email" value={ansprechpartnerForm.email} onChange={handleAnsprechpartnerInputChange('email')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Telefonnummer" value={ansprechpartnerForm.telefonnummer} onChange={handleAnsprechpartnerInputChange('telefonnummer')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Mobilnummer" value={ansprechpartnerForm.mobilnummer} onChange={handleAnsprechpartnerInputChange('mobilnummer')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Position/Funktion" value={ansprechpartnerForm.position_funktion} onChange={handleAnsprechpartnerInputChange('position_funktion')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Abteilung" value={ansprechpartnerForm.abteilung} onChange={handleAnsprechpartnerInputChange('abteilung')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Büro/Standort" value={ansprechpartnerForm.buero_standort} onChange={handleAnsprechpartnerInputChange('buero_standort')} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Notizen" value={ansprechpartnerForm.notizen} onChange={handleAnsprechpartnerInputChange('notizen')} multiline rows={3} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAnsprechpartnerDialogOpen(false);
            resetAnsprechpartnerForm();
          }}>
            Abbrechen
          </Button>
          <Button
            onClick={handleSaveAnsprechpartner}
            variant="contained"
            disabled={!ansprechpartnerForm.vorname || !ansprechpartnerForm.nachname}
          >
            {editingAnsprechpartner ? 'Speichern' : 'Hinzufügen'}
          </Button>
        </DialogActions>
      </Dialog>

      {!isNew && id && (
        <HistorieTimeline
          entitaetTyp="Kunde"
          entitaetId={parseInt(id)}
          open={historieOpen}
          onClose={() => setHistorieOpen(false)}
        />
      )}

      {/* Ansprechpartner löschen Bestätigung */}
      <ConfirmDialog
        open={deleteAnsprechpartnerDialogOpen}
        title="Ansprechpartner löschen"
        message={`Möchten Sie den Ansprechpartner "${ansprechpartnerToDelete ? `${ansprechpartnerToDelete.vorname} ${ansprechpartnerToDelete.nachname}` : ''}" wirklich löschen?`}
        onConfirm={handleDeleteAnsprechpartnerConfirm}
        onCancel={() => {
          setDeleteAnsprechpartnerDialogOpen(false);
          setAnsprechpartnerToDelete(null);
        }}
      />
    </Box>
  );
};

export default KundeDetailPage;
